<script setup>
import { useLayout } from '@/layout/common/composables/layout';
import Sidebar from '@/layout/main/sidebar';
import { Link, router, usePage } from '@inertiajs/vue3'
import { useEnhancedLoading } from '@/composables/useEnhancedLoading';
import { computed, getCurrentInstance } from 'vue';
import logoUrl from '@/assets/login-logo.png';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const props = defineProps({
    menuItems: {
        type: Array,
        required: true
    },
    dashboardRoute: {
        type: String,
        required: true
    }
});

const { calculatedMenuMode } = useLayout();
const { showPageLoading, hidePageLoading } = useEnhancedLoading();
const page = usePage();

// Get orders in production count from shared Inertia props
const ordersInProductionCount = computed(() => {
    return page.props?.ordersInProductionCount || 0;
});

// Reactive current page URL and component
const currentPageUrl = computed(() => page.url);
const currentComponent = computed(() => page.component);

// Computed property for checking if a menu item is active
const isItemActive = computed(() => {
    const url = currentPageUrl.value;
    const component = currentComponent.value;

    // Return a function that checks if an item is active
    return (item) => {
        if (!item.route) return false;

        try {
            // Use Inertia's route().current() which should be reactive
            const isCurrentRoute = route().current(item.route);

            if (!isCurrentRoute) {
                return false;
            }

            // Additional param checks for filtered routes
            if (item.params && item.params.filter) {
                const urlParams = new URLSearchParams(url.split('?')[1] || '');

                // Check favorites parameter
                if (item.params.filter.favorites !== undefined) {
                    const hasFavorites = urlParams.has('filter[favorites]');
                    if (item.params.filter.favorites && !hasFavorites) {
                        return false;
                    }
                }

                // Check interests parameter
                if (item.params.filter.interests !== undefined) {
                    const hasInterests = urlParams.has('filter[interests]');
                    if (item.params.filter.interests && !hasInterests) {
                        return false;
                    }
                }
            }

            return true;
        } catch (e) {
            console.error('Error checking active state:', e);
            return false;
        }
    };
});

// Use isItemActive as the function name in templates
const isMenuItemActive = isItemActive;

// Function to get the href for menu items
const getMenuItemHref = (item) => {
    if (item.route) {
        return route(item.route, item.params || {});
    }
    return item.url || item.to || '#';
};

// Function to handle navigation with loading
const handleNavigation = (event, item) => {
    if (item.route) {
        event.preventDefault(); // Prevent default link navigation
        console.log('Sidebar navigation to:', route(item.route, item.params || {}));
        // Navigation loading is now handled globally in app.js
        router.visit(route(item.route, item.params || {}));
    }
};

// Check if this is the "My Orders" menu item
const isMyOrdersItem = (item) => {
    return item.route === 'client.orders.index';
};

// Get badge count for specific menu items
const getBadgeCount = (item) => {
    if (isMyOrdersItem(item) && ordersInProductionCount.value > 0) {
        return ordersInProductionCount.value;
    }
    return null;
};

// Get tooltip text for badges
const getBadgeTooltip = (item) => {
    if (isMyOrdersItem(item) && ordersInProductionCount.value > 0) {
        const count = ordersInProductionCount.value;
        return count === 1
            ? $t('1 article pending production')
            : `${count} ${$t('articles pending production')}`;
    }
    return null;
};

// Handle badge click to filter orders
const handleBadgeClick = (event, item) => {
    event.preventDefault();
    event.stopPropagation();

    if (isMyOrdersItem(item)) {
        // Navigate to orders page with PRODUCTION status filter
        router.visit(route('client.orders.index', { filter: { status: 'PRODUCTION' } }));
    }
};
</script>

<template>
    <Sidebar>
        <!-- Header -->
        <Sidebar.Header>
            <Link :href="route(dashboardRoute)" class="flex items-center gap-2">
                <div class="flex items-center justify-center">
                    <img :src="logoUrl" class="h-14 w-auto" alt="Logo" />
                </div>
            </Link>
        </Sidebar.Header>
        <!-- Content -->
        <Sidebar.Content v-if="calculatedMenuMode !== 'slim' && calculatedMenuMode !== 'compact' && calculatedMenuMode !== 'horizontal'">
            <Sidebar.Group v-for="menuItem in menuItems" :key="menuItem.label">
                <Sidebar.GroupLabel>{{ $t(menuItem.label) }}</Sidebar.GroupLabel>
                <Sidebar.GroupContent>
                    <Sidebar.Menu>
                        <Sidebar.MenuItem v-for="item in menuItem.items" :key="item.label" :collapsible="!!item.items">
                            <Sidebar.MenuButton
                                :href="getMenuItemHref(item)"
                                :target="item.target"
                                :is-active="isMenuItemActive(item)"
                                :is-first="true"
                                @click="($event) => handleNavigation($event, item)"
                                class="group"
                            >
                                <i :class="item.icon" class="!leading-none !text-sm" />
                                <span class="flex-1">{{ $t(item.label) }}</span>
                                <span
                                    v-if="getBadgeCount(item)"
                                    v-tooltip.top="getBadgeTooltip(item)"
                                    @click="($event) => handleBadgeClick($event, item)"
                                    :class="[
                                        'ml-auto flex h-5 min-w-[20px] px-1.5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white transition-all shadow-sm cursor-pointer',
                                        isMenuItemActive(item)
                                            ? 'bg-red-500 hover:bg-red-600'
                                            : 'bg-red-500/60 hover:bg-red-500'
                                    ]"
                                >
                                    {{ getBadgeCount(item) }}
                                </span>
                            </Sidebar.MenuButton>
                            <Sidebar.Menu v-if="item.items">
                                <Sidebar.MenuItem v-for="subItem in item.items" :key="subItem.label">
                                    <Sidebar.MenuButton
                                        :href="getMenuItemHref(subItem)"
                                        :target="subItem.target"
                                        :is-active="isMenuItemActive(subItem)"
                                        @click="($event) => handleNavigation($event, subItem)"
                                    >
                                        <i :class="subItem.icon" class="!leading-none !text-sm" />
                                        <span>{{ subItem.label }}</span>
                                    </Sidebar.MenuButton>
                                </Sidebar.MenuItem>
                            </Sidebar.Menu>
                        </Sidebar.MenuItem>
                    </Sidebar.Menu>
                </Sidebar.GroupContent>
            </Sidebar.Group>
        </Sidebar.Content>
        <Sidebar.Content v-else>
            <Sidebar.Group>
                <Sidebar.GroupContent>
                    <Sidebar.Menu>
                        <Sidebar.MenuItem v-for="item in menuItems" :key="item.label">
                            <Sidebar.Popover :placement="calculatedMenuMode === 'horizontal' ? 'bottom' : 'right'">
                                <template #trigger>
                                    <Sidebar.MenuButton
                                        :href="getMenuItemHref(item)"
                                        :target="item.target"
                                        :is-active="isMenuItemActive(item)"
                                        :tooltip-value="calculatedMenuMode === 'compact' ? item.label : ''"
                                        @click="($event) => handleNavigation($event, item)"
                                    >
                                        <i :class="item.icon" class="!leading-none !text-sm" />
                                        <span>{{ item.label }}</span>
                                    </Sidebar.MenuButton>
                                </template>
                                <template #content v-if="item.items">
                                    <Sidebar.Menu mode="static">
                                        <Sidebar.MenuItem v-for="subItem in item.items" :key="subItem.label" :collapsible="!!subItem.items">
                                            <Sidebar.MenuButton
                                                mode="static"
                                                :href="getMenuItemHref(subItem)"
                                                :target="subItem.target"
                                                :is-active="isMenuItemActive(subItem)"
                                                @click="($event) => handleNavigation($event, subItem)"
                                                class="group"
                                            >
                                                <i :class="subItem.icon" class="!leading-none !text-sm" />
                                                <span class="flex-1">{{ subItem.label }}</span>
                                                <span
                                                    v-if="getBadgeCount(subItem)"
                                                    v-tooltip.top="getBadgeTooltip(subItem)"
                                                    @click="($event) => handleBadgeClick($event, subItem)"
                                                    :class="[
                                                        'ml-auto flex h-5 min-w-[20px] px-1.5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white transition-all shadow-sm cursor-pointer',
                                                        isMenuItemActive(subItem)
                                                            ? 'bg-red-500 hover:bg-red-600'
                                                            : 'bg-red-500/60 hover:bg-red-500'
                                                    ]"
                                                >
                                                    {{ getBadgeCount(subItem) }}
                                                </span>
                                            </Sidebar.MenuButton>
                                            <Sidebar.Menu mode="static" v-if="subItem.items">
                                                <Sidebar.MenuItem v-for="subSubItem in subItem.items" :key="subSubItem.label">
                                                    <Sidebar.MenuButton
                                                        mode="static"
                                                        :href="getMenuItemHref(subSubItem)"
                                                        :target="subSubItem.target"
                                                        :is-active="isMenuItemActive(subSubItem)"
                                                        @click="($event) => handleNavigation($event, subSubItem)"
                                                    >
                                                        <i :class="subSubItem.icon" class="!leading-none !text-sm" />
                                                        <span>{{ subSubItem.label }}</span>
                                                    </Sidebar.MenuButton>
                                                </Sidebar.MenuItem>
                                            </Sidebar.Menu>
                                        </Sidebar.MenuItem>
                                    </Sidebar.Menu>
                                </template>
                            </Sidebar.Popover>
                        </Sidebar.MenuItem>
                    </Sidebar.Menu>
                </Sidebar.GroupContent>
            </Sidebar.Group>
        </Sidebar.Content>
    </Sidebar>
</template>
